import * as i0 from '@angular/core';
import { Injectable, Component, Input, inject, Inject, NgModule } from '@angular/core';
import * as i1 from '@ngrx/store';
import { createAction, props, createFeatureSelector, createSelector, select, createReducer, on, StoreModule } from '@ngrx/store';
import { filter, map, switchMap, catchError } from 'rxjs/operators';
import { get } from 'lodash';
import * as i2 from '@angular/common';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import * as i2$1 from '@ngrx/effects';
import { createEffect, ofType, EffectsModule } from '@ngrx/effects';
import * as i1$1 from '@angular/common/http';
import { COMMON_APP_CONFIG } from '@rogers/self-serve-common';
import { of } from 'rxjs';
const _c0 = [[["", "hasUserPermission", ""]], [["", "noUserPermission", ""]]];
const _c1 = ["[hasUserPermission]", "[noUserPermission]"];
function HasUserPermissionComponent_Conditional_0_Template(rf, ctx) {
  if (rf & 1) {
    i0.ɵɵprojection(0);
  }
}
function HasUserPermissionComponent_Conditional_2_Template(rf, ctx) {
  if (rf & 1) {
    i0.ɵɵprojection(0, 1);
  }
}
var UserPermissionsActionTypes = /*#__PURE__*/function (UserPermissionsActionTypes) {
  UserPermissionsActionTypes["LoadUserPermissions"] = "[Global] load user permissions from DS";
  UserPermissionsActionTypes["LoadUserPermissionsSuccess"] = "[Global] load user permissions successful";
  UserPermissionsActionTypes["LoadUserPermissionsFail"] = "[Global] load user permissions failed";
  UserPermissionsActionTypes["ResetUserPermissions"] = "[Global] Reset user permissions in the store";
  return UserPermissionsActionTypes;
}(UserPermissionsActionTypes || {});
const loadUserPermissions = createAction(UserPermissionsActionTypes.LoadUserPermissions);
const loadUserPermissionsFailed = createAction(UserPermissionsActionTypes.LoadUserPermissionsFail, props());
const loadUserPermissionsSuccess = createAction(UserPermissionsActionTypes.LoadUserPermissionsSuccess, props());
const resetUserPermissions = createAction(UserPermissionsActionTypes.ResetUserPermissions);
const getUserPermissionEntity = createFeatureSelector('userPermissions');
const getUserPermissions = createSelector(getUserPermissionEntity, userPermissions => {
  return get(userPermissions, 'userPermissions', []);
});
let UserPermissionFacadeService = /*#__PURE__*/(() => {
  class UserPermissionFacadeService {
    constructor(store) {
      this.store = store;
    }
    hasUserPermission$(userPermissionsId) {
      return this.store.pipe(select(getUserPermissions), filter(userPermissions => !!userPermissions), map(userPermissions => userPermissions.some(userPermission => userPermission === userPermissionsId)));
    }
    loadUserPermissions() {
      return this.store.dispatch(loadUserPermissions());
    }
    resetUserPermissions() {
      return this.store.dispatch(resetUserPermissions());
    }
    static {
      this.ɵfac = function UserPermissionFacadeService_Factory(t) {
        return new (t || UserPermissionFacadeService)(i0.ɵɵinject(i1.Store));
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: UserPermissionFacadeService,
        factory: UserPermissionFacadeService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return UserPermissionFacadeService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
let HasUserPermissionComponent = /*#__PURE__*/(() => {
  class HasUserPermissionComponent {
    constructor(userPermissionFacadeService) {
      this.userPermissionFacadeService = userPermissionFacadeService;
    }
    ngOnInit() {
      this.hasUserPermission$ = this.userPermissionFacadeService.hasUserPermission$(this.switchID);
    }
    static {
      this.ɵfac = function HasUserPermissionComponent_Factory(t) {
        return new (t || HasUserPermissionComponent)(i0.ɵɵdirectiveInject(UserPermissionFacadeService));
      };
    }
    static {
      this.ɵcmp = /* @__PURE__ */i0.ɵɵdefineComponent({
        type: HasUserPermissionComponent,
        selectors: [["ss-has-user-permission"]],
        inputs: {
          switchID: "switchID"
        },
        ngContentSelectors: _c1,
        decls: 3,
        vars: 3,
        template: function HasUserPermissionComponent_Template(rf, ctx) {
          if (rf & 1) {
            i0.ɵɵprojectionDef(_c0);
            i0.ɵɵtemplate(0, HasUserPermissionComponent_Conditional_0_Template, 1, 0);
            i0.ɵɵpipe(1, "async");
            i0.ɵɵtemplate(2, HasUserPermissionComponent_Conditional_2_Template, 1, 0);
          }
          if (rf & 2) {
            i0.ɵɵconditional(0, i0.ɵɵpipeBind1(1, 1, ctx.hasUserPermission$) ? 0 : 2);
          }
        },
        dependencies: [i2.AsyncPipe],
        encapsulation: 2
      });
    }
  }
  return HasUserPermissionComponent;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
const hasUserPermissionGuard = (permissionSwitchId, fallbackRoute = '') => () => {
  const router = inject(Router);
  const userPermissionFacadeService = inject(UserPermissionFacadeService);
  return userPermissionFacadeService.hasUserPermission$(permissionSwitchId).pipe(map(hasPermission => {
    if (hasPermission) {
      return hasPermission;
    }
    return fallbackRoute ? router.createUrlTree([fallbackRoute.startsWith('/') ? fallbackRoute : `/${fallbackRoute}`]) : hasPermission;
  }));
};
let UserPermissionEffects = /*#__PURE__*/(() => {
  class UserPermissionEffects {
    constructor(http, actions$, appConfig) {
      this.http = http;
      this.actions$ = actions$;
      this.appConfig = appConfig;
      this.loadUserPermissions$ = createEffect(() => this.actions$.pipe(ofType(UserPermissionsActionTypes.LoadUserPermissions), switchMap(() => {
        return this.getUserPermissions().pipe(map(userPermissionsResponse => {
          const userPermissions = get(userPermissionsResponse, 'content.permissions', []);
          return loadUserPermissionsSuccess({
            userPermissions
          });
        }), catchError(error => {
          const errorPayload = this.appConfig.getErrorTrackingObject(error);
          return of(loadUserPermissionsFailed({
            error: errorPayload
          }));
        }));
      })));
    }
    getUserPermissions() {
      const permissionsApiUrl = `${this.appConfig.contentBaseUrl}permissions`;
      return this.http.get(permissionsApiUrl, {
        headers: this.appConfig.getHeaders()
      });
    }
    static {
      this.ɵfac = function UserPermissionEffects_Factory(t) {
        return new (t || UserPermissionEffects)(i0.ɵɵinject(i1$1.HttpClient), i0.ɵɵinject(i2$1.Actions), i0.ɵɵinject(COMMON_APP_CONFIG));
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: UserPermissionEffects,
        factory: UserPermissionEffects.ɵfac
      });
    }
  }
  return UserPermissionEffects;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
const userPermissionsStateName = 'userPermissions';
const initialState = {
  userPermissions: [],
  loading: false,
  loaded: false,
  error: null
};
const createUserPermissionsReducer = createReducer(initialState, on(loadUserPermissions, _state => ({
  ...initialState,
  loading: true
})), on(loadUserPermissionsFailed, (_state, {
  error
}) => ({
  ...initialState,
  error,
  loaded: false,
  loading: false
})), on(loadUserPermissionsSuccess, (_state, {
  userPermissions
}) => ({
  ...initialState,
  userPermissions,
  loaded: true,
  loading: false
})), on(resetUserPermissions, _ => initialState));
// tslint:disable-next-line:only-arrow-functions
function userPermissionsReducer(state, action) {
  return createUserPermissionsReducer(state, action);
}
let UserPermissionStoreModule = /*#__PURE__*/(() => {
  class UserPermissionStoreModule {
    static {
      this.ɵfac = function UserPermissionStoreModule_Factory(t) {
        return new (t || UserPermissionStoreModule)();
      };
    }
    static {
      this.ɵmod = /* @__PURE__ */i0.ɵɵdefineNgModule({
        type: UserPermissionStoreModule
      });
    }
    static {
      this.ɵinj = /* @__PURE__ */i0.ɵɵdefineInjector({
        imports: [CommonModule, StoreModule.forFeature(userPermissionsStateName, userPermissionsReducer), EffectsModule.forFeature([UserPermissionEffects])]
      });
    }
  }
  return UserPermissionStoreModule;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
let UserPermissionModule = /*#__PURE__*/(() => {
  class UserPermissionModule {
    static {
      this.ɵfac = function UserPermissionModule_Factory(t) {
        return new (t || UserPermissionModule)();
      };
    }
    static {
      this.ɵmod = /* @__PURE__ */i0.ɵɵdefineNgModule({
        type: UserPermissionModule
      });
    }
    static {
      this.ɵinj = /* @__PURE__ */i0.ɵɵdefineInjector({
        imports: [CommonModule]
      });
    }
  }
  return UserPermissionModule;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * Generated bundle index. Do not edit.
 */

export { HasUserPermissionComponent, UserPermissionEffects, UserPermissionFacadeService, UserPermissionModule, UserPermissionStoreModule, UserPermissionsActionTypes, getUserPermissionEntity, getUserPermissions, hasUserPermissionGuard, loadUserPermissions, loadUserPermissionsFailed, loadUserPermissionsSuccess, resetUserPermissions, userPermissionsReducer, userPermissionsStateName };
